import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type { IntlService } from "ember-intl";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import tWithDefault from "parog-web/utils/t-with-default";

//---- Automatically generated, don't update this part ----

type StomachPainStringEnum =
  | "0"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10";

type UrgentStoolsMovementsStringEnum =
  | "0"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10";

type SocialDifficultiesStringEnum =
  | "0"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10";

type WorkDifficultiesStringEnum =
  | "0"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10";

type SleepDifficultiesStringEnum =
  | "0"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10";

type LackOfEnergyStringEnum =
  | "0"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10";

type LowMoodStringEnum =
  | "0"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10";

type BodyDissatisfactionStringEnum =
  | "0"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10";

type SexualDifficultiesStringEnum =
  | "0"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10";

type ArticularPainIbdStringEnum =
  | "0"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9"
  | "10";

export default class IbdDiskSurveyFilledJournalEvent extends JournalEvent {
  static override type = "ibd-disk-survey-filled-journal-event";

  @attr("string") declare stomachPain: StomachPainStringEnum;

  @attr("string")
  declare urgentStoolsMovements: UrgentStoolsMovementsStringEnum;

  @attr("string") declare socialDifficulties: SocialDifficultiesStringEnum;

  @attr("string") declare workDifficulties: WorkDifficultiesStringEnum;

  @attr("string") declare sleepDifficulties: SleepDifficultiesStringEnum;

  @attr("string") declare lackOfEnergy: LackOfEnergyStringEnum;

  @attr("string") declare lowMood: LowMoodStringEnum;

  @attr("string") declare bodyDissatisfaction: BodyDissatisfactionStringEnum;

  @attr("string") declare sexualDifficulties: SexualDifficultiesStringEnum;

  @attr("string") declare articularPainIbd: ArticularPainIbdStringEnum;

  @attr("number") declare score: number;

  static override validations(intl: IntlService) {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.ibd-disk-survey-filled-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      score: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.ibd-disk-survey-filled-journal-event.score"
        )
      }),
      stomachPain: validateInclusion({
        list: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
        allowBlank: true,
        description: intl.t(
          "validations.ibd-disk-survey-filled-journal-event.stomach-pain"
        )
      }),
      urgentStoolsMovements: validateInclusion({
        list: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
        allowBlank: true,
        description: intl.t(
          "validations.ibd-disk-survey-filled-journal-event.urgent-stools-movements"
        )
      }),
      socialDifficulties: validateInclusion({
        list: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
        allowBlank: true,
        description: intl.t(
          "validations.ibd-disk-survey-filled-journal-event.social-difficulties"
        )
      }),
      workDifficulties: validateInclusion({
        list: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
        allowBlank: true,
        description: intl.t(
          "validations.ibd-disk-survey-filled-journal-event.work-difficulties"
        )
      }),
      sleepDifficulties: validateInclusion({
        list: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
        allowBlank: true,
        description: intl.t(
          "validations.ibd-disk-survey-filled-journal-event.sleep-difficulties"
        )
      }),
      lackOfEnergy: validateInclusion({
        list: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
        allowBlank: true,
        description: intl.t(
          "validations.ibd-disk-survey-filled-journal-event.lack-of-energy"
        )
      }),
      lowMood: validateInclusion({
        list: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
        allowBlank: true,
        description: intl.t(
          "validations.ibd-disk-survey-filled-journal-event.low-mood"
        )
      }),
      bodyDissatisfaction: validateInclusion({
        list: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
        allowBlank: true,
        description: intl.t(
          "validations.ibd-disk-survey-filled-journal-event.body-dissatisfaction"
        )
      }),
      sexualDifficulties: validateInclusion({
        list: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
        allowBlank: true,
        description: intl.t(
          "validations.ibd-disk-survey-filled-journal-event.sexual-difficulties"
        )
      }),
      articularPainIbd: validateInclusion({
        list: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
        allowBlank: true,
        description: intl.t(
          "validations.ibd-disk-survey-filled-journal-event.articular-pain-ibd"
        )
      })
    };
  }

  //---- End of generated code ----

  get points(): number[] {
    return IbdDiskSurveyFilledJournalEvent.sections.map(key =>
      Number(this[key])
    );
  }

  // Order is important, don't change it.
  static sections: string[] = [
    "stomachPain",
    "urgentStoolsMovements",
    "socialDifficulties",
    "workDifficulties",
    "sleepDifficulties",
    "lackOfEnergy",
    "lowMood",
    "bodyDissatisfaction",
    "sexualDifficulties",
    "articularPainIbd"
  ];

  @belongsTo("patient", {
    async: false,
    inverse: "ibdDiskSurveyFilledJournalEvents"
  })
  declare patient: Patient;
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "ibd-disk-survey-filled-journal-event": IbdDiskSurveyFilledJournalEvent;
  }
}
